import { ArrowLeft, Check, Plus, Search, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getAttributesRequest, selectAttributes } from 'store/attributes';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useToast } from '../../contexts/ToastContext';
import {
  clearContactState,
  createContactRequest,
  getContactRequest,
  updateContactRequest,
} from '../../store/contacts/contacts.action';
import {
  selectContactsError,
  selectContactsLoading,
  selectContactsSuccess,
  selectSelectedContact,
} from '../../store/contacts/contacts.selector';
import { CreateContactRequest } from '../../types/contacts/contacts.types';

interface CustomAttribute {
  name: string;
  value: string;
}

const ContactForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  // Redux state
  const loading = useSelector(selectContactsLoading);
  const error = useSelector(selectContactsError);
  const contact = useSelector(selectSelectedContact);
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    custom_id: '',
    _id: '',
  });
  const [customAttributes, setCustomAttributes] = useState<CustomAttribute[]>([
    { name: '', value: '' },
  ]);
  const attributesData = useSelector(selectAttributes);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const success = useSelector(selectContactsSuccess);

  // Right sidebar (attribute builder) state
  const [attributeSearch, setAttributeSearch] = useState('');
  // Load contact data for editing
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(getContactRequest(id));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    dispatch(getAttributesRequest());
  }, [dispatch]);

  // Clear success state when component unmounts
  useEffect(() => {
    return () => {
      if (success) {
        dispatch(clearContactState());
      }
    };
  }, [success, dispatch]);

  // Populate form when contact data is loaded for editing
  useEffect(() => {
    if (contact && isEditMode) {
      setFormData({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
        custom_id: contact.custom_id || '',
        _id: contact._id || '',
      });

      // Convert attributes object to array of name-value pairs
      const attributesArray = contact.attributes
        ? Object.entries(contact.attributes).map(([name, value]) => ({
            name,
            value:
              typeof value !== 'string' ? JSON.stringify(value) || '' : value,
          }))
        : [{ name: '', value: '' }];

      setCustomAttributes(
        attributesArray.length > 0 ? attributesArray : [{ name: '', value: '' }]
      );
    }
  }, [contact, isEditMode]);

  // Show toast notification when there's an error
  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: error,
      });
      dispatch(clearContactState());
    }
  }, [error, showToast, dispatch]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      delete errors[field];
      setErrors(errors);
    }

    if (field === 'email' && value.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          email: 'Please enter a valid email address',
        }));
      }
    }
  };

  const handleCustomAttributeChange = (
    index: number,
    field: 'name' | 'value',
    value: string
  ) => {
    const newAttributes = [...customAttributes];
    newAttributes[index][field] = value;
    setCustomAttributes(newAttributes);
  };

  useEffect(() => {
    if (customAttributes && customAttributes.length > 0) {
      const duplicateAttributes = customAttributes.reduce(
        (acc, attr) => {
          if (acc[attr.name]) {
            acc[attr.name]++;
          } else {
            acc[attr.name] = 1;
          }
          return acc;
        },
        {} as { [key: string]: number }
      );
      const duplicateAttributeNames = Object.keys(duplicateAttributes).filter(
        (key) => duplicateAttributes[key] > 1
      );
      if (duplicateAttributeNames.length > 0) {
        setErrors((prev) => ({
          ...prev,
          duplicate_attribute: `Attribute name ${duplicateAttributeNames.join(', ')} already exists, please enter a different name`,
        }));
      } else {
        setErrors((prev) => {
          const { duplicate_attribute, ...rest } = prev;
          return rest;
        });
      }
    }
  }, [customAttributes]);

  const addCustomAttribute = () => {
    setCustomAttributes([...customAttributes, { name: '', value: '' }]);
  };

  const removeCustomAttribute = (index: number) => {
    if (customAttributes.length > 1) {
      const newAttributes = customAttributes.filter((_, i) => i !== index);
      setCustomAttributes(newAttributes);
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (success) {
      showToast({
        type: 'success',
        title: isEditMode ? 'Contact Updated' : 'Contact Added',
        message: isEditMode
          ? 'Contact has been updated successfully'
          : 'Contact has been added successfully',
      });

      // Navigate first, then clear success state after a short delay
      navigate('/contacts');
      setTimeout(() => {
        dispatch(clearContactState());
      }, 100);
    }
  }, [success, showToast, dispatch, navigate, isEditMode]);

  const handleSubmit = () => {
    if (validateForm()) {
      // Convert custom attributes array back to object
      const attributesObject = customAttributes.reduce(
        (acc, attr) => {
          if (attr.name.trim() === '') {
            return acc;
          }
          acc[attr.name] = attr.value?.toString() || '';
          return acc;
        },
        {} as { [key: string]: any }
      );

      const contactData: CreateContactRequest = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        attributes: attributesObject,
        source: 'manual',
        ...(formData.custom_id && { custom_id: formData.custom_id }),
      };
      dispatch(
        isEditMode
          ? updateContactRequest({ id: formData._id, data: contactData })
          : createContactRequest(contactData)
      );
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const pageTitle = isEditMode ? 'Edit Contact' : 'Add New Contact';
  const submitButtonText = isEditMode ? 'Update Contact' : 'Add Contact';

  const filteredAttributes =
    attributesData?.attributes?.filter(
      (item) =>
        !item.isDeleted &&
        (attributeSearch.trim() === '' ||
          item.name.toLowerCase().includes(attributeSearch.toLowerCase()) ||
          (item.label &&
            item.label.toLowerCase().includes(attributeSearch.toLowerCase())))
    ) ?? [];

  return (
    <div className="h-[calc(100vh-60px)] flex bg-gray-50">
      {/* Main content: Header + Form */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Header */}
        <div className="flex-shrink-0 space-y-4 p-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="medium"
              onClick={handleCancel}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="h-6 w-px bg-border" />
            <h1 className="text-xl font-semibold text-foreground">
              {pageTitle}
            </h1>
          </div>
        </div>
        <div className="flex-1 overflow-hidden pb-6 relative mb-10">
          <div className="h-full flex flex-col overflow-y-auto scrollbar-thin">
            <div className="flex flex-col mx-6 mb-6 gap-6 rounded-lg border border-border bg-card shadow-sm ">
              {/* Basic Information */}
              <div className="">
                <div className="flex items-center gap-3 bg-muted/60 px-4 py-3 border-b border-border">
                  <div
                    className="h-8 w-0.5 rounded-full bg-primary"
                    aria-hidden
                  />
                  <span className="text-sm font-semibold text-foreground tracking-tight">
                    Basic Information
                  </span>
                </div>
                <div className="flex flex-col gap-5 p-4 md:p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-foreground">
                        First Name
                      </label>
                      <Input
                        placeholder="Enter first name"
                        value={formData.firstName}
                        onChange={(e) =>
                          handleInputChange('firstName', e.target.value)
                        }
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-foreground">
                        Last Name
                      </label>
                      <Input
                        placeholder="Enter last name"
                        value={formData.lastName}
                        onChange={(e) =>
                          handleInputChange('lastName', e.target.value)
                        }
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                      Email Address{' '}
                      <span className="text-destructive" aria-label="required">
                        *
                      </span>
                    </label>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange('email', e.target.value)
                      }
                      error={errors.email}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                      Custom ID
                      {!isEditMode && (
                        <span className="text-muted-foreground text-xs font-normal ml-1.5">
                          (Optional)
                        </span>
                      )}
                    </label>
                    <Input
                      placeholder="Enter custom ID (optional)"
                      value={formData.custom_id}
                      onChange={(e) =>
                        handleInputChange('custom_id', e.target.value)
                      }
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Custom Attributes */}
              <div className="">
                <div className="flex items-center justify-between gap-3 bg-muted/60 px-4 py-3 border-y border-border">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-8 w-0.5 rounded-full bg-primary"
                      aria-hidden
                    />
                    <span className="text-sm font-semibold text-foreground tracking-tight">
                      Custom Attributes
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={addCustomAttribute}
                    className="flex items-center gap-2 shrink-0 border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary"
                  >
                    <Plus className="w-4 h-4" />
                    Add Attribute
                  </Button>
                </div>
                <div className="flex flex-col gap-4 p-4 md:p-5">
                  {customAttributes.map((attribute, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-center"
                    >
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-foreground">
                          Attribute name
                        </label>
                        <Input
                          placeholder="e.g. plan, company, role"
                          value={attribute.name}
                          onChange={(e) =>
                            handleCustomAttributeChange(
                              index,
                              'name',
                              e.target.value
                            )
                          }
                          className="w-full bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-foreground">
                          Attribute value
                        </label>
                        <Input
                          placeholder="e.g. pro, Acme Corp, manager"
                          value={attribute.value}
                          onChange={(e) =>
                            handleCustomAttributeChange(
                              index,
                              'value',
                              e.target.value
                            )
                          }
                          className="w-full bg-background"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCustomAttribute(index)}
                        className="p-2.5 text-muted-foreground hover:text-destructive transition-colors rounded-lg hover:bg-destructive/10 border border-transparent hover:border-destructive/20 self-end md:self-auto disabled:opacity-40 disabled:pointer-events-none"
                        style={{ outline: 0 }}
                        disabled={customAttributes.length === 1}
                        title="Remove attribute"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {Object.keys(errors).length > 0 &&
                    errors.duplicate_attribute && (
                      <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/10">
                        <p className="text-sm font-medium text-destructive">
                          {errors.duplicate_attribute}
                        </p>
                      </div>
                    )}
                  <div className="p-4 rounded-lg border border-info/30 bg-info/10">
                    <p className="text-sm text-foreground/90">
                      <strong className="text-foreground font-medium">
                        Tip:
                      </strong>{' '}
                      <span className="text-muted-foreground">
                        Custom attributes let you store extra information (e.g.
                        plan type, company size, subscription status).
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 w-full bg-card border-t border-border px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2">
              <button
                className="bg-primary flex items-center gap-2 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary hover:border-primary transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || Object.keys(errors).length > 0}
                onClick={handleSubmit}
              >
                {loading ? 'Saving...' : submitButtonText}
              </button>
              <button
                className="bg-white text-destructive border border-destructive px-4 py-2 rounded-lg font-medium hover:bg-destructive hover:text-white hover:border-destructive transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right sidebar: Attribute builder */}
      <aside className="w-[330px] flex-shrink-0 border-l border-border bg-card flex flex-col overflow-hidden">
        {/* Sidebar header */}
        <div className="flex-shrink-0 p-4 border-b border-border">
          <div className="flex rounded-lg bg-muted p-0.5">
            <div
              className={`flex-1 flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors bg-primary/10 text-wh shadow-sm`}
            >
              Attributes
            </div>
          </div>
        </div>

        <>
          <div className="flex-1 overflow-y-auto scrollbar-thin px-4 pb-4">
            <div className="flex-shrink-0 py-3">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search"
                  value={attributeSearch}
                  onChange={(e) => setAttributeSearch(e.target.value)}
                  className="w-full pl-3 h-9 text-sm focus:ring-0 focus:outline-none focus:border-primary"
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>
            {filteredAttributes.map((attribute, index) => {
              const isInForm = customAttributes.some(
                (attr) => attr.name === attribute.name
              );
              return (
                <div
                  key={attribute.name}
                  className={`group flex items-start gap-3 rounded-lg border p-3 mb-2 transition-colors hover:border-primary hover:bg-primary/5 cursor-pointer ${
                    isInForm
                      ? 'border-border bg-muted cursor-not-allowed opacity-75'
                      : 'border-border bg-card hover:border-input hover:bg-muted cursor-pointer'
                  }`}
                  onClick={() => {
                    if (!isInForm) {
                      setCustomAttributes([
                        ...customAttributes,
                        { name: attribute.name, value: '' },
                      ]);
                    }
                  }}
                >
                  <div className="flex gap-2 min-w-0">
                    <div className="text-sm font-medium text-foreground">
                      {attribute.label ?? attribute.name}
                    </div>
                    {isInForm && (
                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-success-background text-success">
                        <Check className="w-3 h-3" />
                        In form
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      </aside>
    </div>
  );
};

export default ContactForm;
